import React from "react";
import TripDetails from "@/components/TripDetails";
import { Link } from "react-router-dom";
import { Check, Trophy, Heart, Book, Globe, User, FileText, Star } from "lucide-react";
import Navbar from "@/components/common/Navbar";
import Footer from "@/components/home/Footer";
import MetaTags from '../components/MetaTags';

const About: React.FC = () => {
  const aboutDetails = [
    {
      icon: "calendar" as const,
      title: "20+ Years Experience",
      description: "Leading safaris across Namibia, Botswana, and Victoria Falls since 2000."
    },
    {
      icon: "users" as const,
      title: "Bilingual Expertise",
      description: "Fluent in English and German for international travelers."
    },
    {
      icon: "bed" as const,
      title: "Premium Accommodations",
      description: "Carefully selected lodges for comfort and authentic experiences."
    }
  ];

  // SEO structured data for about page
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Rion Wildlife Safaris",
    "description": "Learn about Professor Rion Haraeb and his 20+ years of experience leading premium safari adventures across Southern Africa.",
    "mainEntity": {
      "@type": "Person",
      "name": "Professor Rion Haraeb",
      "jobTitle": "Safari Guide and Founder",
      "description": "Professor Rion Haraeb has over 20 years of experience guiding safari tours in Namibia, Botswana, and surrounding regions.",
      "knowsLanguage": ["English", "German"],
      "worksFor": {
        "@type": "TravelAgency",
        "name": "Rion Wildlife Safaris",
        "description": "Premium safari experiences across Namibia, Botswana, and Victoria Falls."
      }
    }
  };

  return (
    <>
      <MetaTags
        title="About Us | Rion Wildlife Safaris"
        description="Learn about Professor Rion Haraeb and his 20+ years of experience leading bilingual English/German safari adventures across Southern Africa's most beautiful wildlife destinations."
        canonicalUrl="https://rionwildlifesafaris.com/about"
        structuredData={aboutStructuredData}
      />

      <div className="bg-white flex flex-col overflow-hidden">
        {/* Hero Section with improved styling */}
        <div className="relative h-[65vh] min-h-[500px] flex items-center justify-center mt-0 overflow-hidden bg-gray-900">
          <img
            src="/About images/Hero.jpeg"
            alt="About Rion Wildlife Safaris"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-black/50 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>

          {/* Navbar - absolutely positioned at top */}
          <header className="absolute top-0 left-0 right-0 z-50">
            <Navbar />
          </header>

          {/* Hero content */}
          <div className="relative z-10 text-center text-white px-4 max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white max-w-4xl leading-tight font-display animate-fade-up">About Rion Wildlife Safaris</h1>
            <p className="text-xl text-white/90 max-w-2xl mb-8 animate-fade-up animation-delay-200">
              Experience the majesty of southern Africa with our expert-guided safari adventures
            </p>
          </div>

          {/* Bottom gradient */}
          <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-white to-transparent"></div>
        </div>

        {/* Subpages Cards Section */}
        <section className="py-16 bg-white relative z-10">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 animate-fade-up animation-delay-300">
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                  Explore More About Us
                </h2>
                <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full mb-4"></div>
                <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                  Learn more about our team, our philosophy, and what our guests have to say
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Our Guides Card */}
                <Link to="/about/our-guides" className="group animate-fade-up animation-delay-400">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="h-56 overflow-hidden relative">
                      <img
                        src="/About images/Hero.jpeg"
                        alt="Our Expert Safari Guides"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white mb-2">
                          <User className="h-5 w-5" />
                          <span className="font-medium">Meet Our Team</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 flex-grow">
                      <h3 className="text-xl font-display font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                        Our Expert Safari Guides
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Meet the passionate experts who will lead your African adventure and make your safari experience unforgettable.
                      </p>
                      <div className="flex items-center text-amber-700 font-medium mt-auto">
                        <span>Learn more</span>
                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Our Philosophy Card */}
                <Link to="/about/philosophy" className="group animate-fade-up animation-delay-500">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="h-56 overflow-hidden relative">
                      <img
                        src="/About images/Hero01.jpeg"
                        alt="Our Safari Philosophy"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white mb-2">
                          <FileText className="h-5 w-5" />
                          <span className="font-medium">Our Approach</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 flex-grow">
                      <h3 className="text-xl font-display font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                        Our Safari Philosophy
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Discover how conservation, education, and authentic experiences form the foundation of everything we do.
                      </p>
                      <div className="flex items-center text-amber-700 font-medium mt-auto">
                        <span>Learn more</span>
                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Testimonials Card */}
                <Link to="/testimonials" className="group animate-fade-up animation-delay-600">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="h-56 overflow-hidden relative">
                      <img
                        src="/gallery images/aboutheropic.jpeg"
                        alt="Guest Testimonials"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white mb-2">
                          <Star className="h-5 w-5" />
                          <span className="font-medium">Guest Reviews</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 flex-grow">
                      <h3 className="text-xl font-display font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                        Guest Testimonials
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Hear from travelers who have experienced the magic of Rion Wildlife Safaris across southern Africa.
                      </p>
                      <div className="flex items-center text-amber-700 font-medium mt-auto">
                        <span>Learn more</span>
                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 relative">
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-amber-50/80 to-white/0 z-0"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="animate-fade-up animation-delay-300">
                <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
                  Our Story
                </span>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  Meet Professor Rion Haraeb
                </h2>
                <div className="w-24 h-1 bg-amber-500 rounded-full mb-8"></div>

                <div className="prose prose-lg text-gray-700 max-w-none mb-10">
                  <p className="mb-4">
                    <strong>Rion Wildlife Safaris</strong> offers premium safari experiences across Namibia, Botswana, and Victoria Falls, led by Professor Rion Haraeb. With over 20 years of experience, Rion specializes in bilingual (English and German) guided tours, targeting German and American travelers.
                  </p>
                  <p className="mb-4">
                    What sets our safaris apart is not just the destinations we visit, but the depth of knowledge and personal connection that Professor Rion brings to each journey. As a passionate conservationist and educator, Rion transforms each safari into an immersive learning experience.
                  </p>
                  <p>
                    All our tours feature premium lodging, expert wildlife viewing, and cultural highlights designed to showcase the best of southern Africa's natural beauty and heritage.
                  </p>
                </div>

                <Link
                  to="/about/our-guides"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white font-medium rounded-lg hover:bg-amber-800 transition-colors"
                >
                  Meet Your Guide
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>

              <div className="relative animate-fade-up animation-delay-500">
                <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/About images/Rionimage.jpeg"
                    alt="Professor Rion Haraeb"
                    loading="lazy"
                    className="w-full h-[600px] object-cover"
                  />
                </div>
                <div className="absolute bottom-10 right-10 bg-white rounded-xl p-5 shadow-xl max-w-xs z-20 animate-fade-up animation-delay-700">
                  <div className="flex items-center gap-3 mb-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    <span className="font-medium text-gray-900">Safari Expert</span>
                  </div>
                  <p className="text-gray-700">
                    "My mission is to share the wonders of African wildlife while promoting conservation and cultural understanding."
                  </p>
                  <div className="mt-4 flex justify-end">
                    <span className="font-display font-bold text-amber-800">— Prof. Rion Haraeb</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TripDetails details={aboutDetails} />

        <section className="py-20 bg-amber-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-up">
              <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
                Why Choose Us
              </span>
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                What Sets Us Apart
              </h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-200">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                  <Globe className="h-7 w-7 text-amber-700" />
                </div>
                <h3 className="text-xl font-display font-bold text-gray-900 mb-4">
                  Local Expertise
                </h3>
                <p className="text-gray-700">
                  Born and raised in Namibia, Rion brings authentic insights and access to locations off the beaten path.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-300">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                  <Book className="h-7 w-7 text-amber-700" />
                </div>
                <h3 className="text-xl font-display font-bold text-gray-900 mb-4">
                  Educational Focus
                </h3>
                <p className="text-gray-700">
                  Learn about ecosystems, conservation efforts, and wildlife behavior from an experienced professor.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-400">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                  <Heart className="h-7 w-7 text-amber-700" />
                </div>
                <h3 className="text-xl font-display font-bold text-gray-900 mb-4">
                  Personalized Service
                </h3>
                <p className="text-gray-700">
                  Small group sizes ensure personalized attention and a safari experience tailored to your interests.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center animate-fade-up animation-delay-500">
              <Link
                to="/about/philosophy"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white font-medium rounded-lg hover:bg-amber-800 transition-colors"
              >
                Discover Our Philosophy
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section id="partners" className="py-24 bg-amber-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 animate-fade-up">
                <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
                  Our Network
                </span>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  Trusted Partnerships
                </h2>
                <div className="w-24 h-1 bg-amber-500 rounded-full mb-8"></div>

                <div className="prose prose-lg text-gray-700 max-w-none mb-10">
                  <p>
                    Rion Wildlife Safaris collaborates with leading conservation organizations and tourism boards to deliver exceptional safari experiences while supporting essential wildlife protection initiatives throughout Southern Africa.
                  </p>
                  <p>
                    These partnerships enable us to provide unparalleled access to protected areas while ensuring our operations contribute positively to conservation efforts and local communities.
                  </p>
                </div>

                <div className="space-y-6 mb-8">
                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-200">
                    <div className="flex items-start gap-4">
                      <img
                        src="/partners/WhatsApp Image 2025-02-27 at 11.47.19.jpeg"
                        alt="Gondwana Collection"
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-xl font-display font-bold text-gray-900 mb-2">Gondwana Collection</h4>
                        <p className="text-gray-700">
                          Our premier accommodation partner offering authentic lodges and camps throughout Namibia, combining luxury with sustainability principles.
                        </p>
                        <a href="https://www.gondwana-collection.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-amber-700 hover:text-amber-600 mt-2 text-sm font-medium">
                          Visit partner website
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-300">
                    <div className="flex items-start gap-4">
                      <img
                        src="/partners/WhatsApp Image 2025-02-27 at 11.48.06.jpeg"
                        alt="Namibia Tourism Board"
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-xl font-display font-bold text-gray-900 mb-2">Namibia Tourism Board</h4>
                        <p className="text-gray-700">
                          Official governmental partner ensuring the highest quality standards and sustainable tourism practices across our Namibian safaris.
                        </p>
                        <a href="https://www.namibiatourism.com.na/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-amber-700 hover:text-amber-600 mt-2 text-sm font-medium">
                          Visit partner website
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4 animate-fade-up animation-delay-400">
                  <button
                    type="button"
                    id="partner-tab-1"
                    className="px-6 py-2 bg-amber-700 text-white rounded-lg font-medium text-sm active"
                    onClick={() => {
                      document.getElementById('partner-tab-1')?.classList.add('bg-amber-700', 'text-white');
                      document.getElementById('partner-tab-1')?.classList.remove('bg-white', 'text-amber-700');
                      document.getElementById('partner-tab-2')?.classList.add('bg-white', 'text-amber-700');
                      document.getElementById('partner-tab-2')?.classList.remove('bg-amber-700', 'text-white');
                      document.getElementById('partner-content-1')?.classList.remove('hidden');
                      document.getElementById('partner-content-2')?.classList.add('hidden');
                    }}
                  >
                    Lodging & Tourism
                  </button>
                  <button
                    type="button"
                    id="partner-tab-2"
                    className="px-6 py-2 bg-white text-amber-700 rounded-lg font-medium text-sm"
                    onClick={() => {
                      document.getElementById('partner-tab-2')?.classList.add('bg-amber-700', 'text-white');
                      document.getElementById('partner-tab-2')?.classList.remove('bg-white', 'text-amber-700');
                      document.getElementById('partner-tab-1')?.classList.add('bg-white', 'text-amber-700');
                      document.getElementById('partner-tab-1')?.classList.remove('bg-amber-700', 'text-white');
                      document.getElementById('partner-content-2')?.classList.remove('hidden');
                      document.getElementById('partner-content-1')?.classList.add('hidden');
                    }}
                  >
                    Conservation
                  </button>
                </div>
              </div>

              <div className="order-1 lg:order-2 animate-fade-up animation-delay-300">
                <div id="partner-content-1" className="relative">
                  <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="/partners/WhatsApp Image 2025-02-27 at 11.47.19.jpeg"
                      alt="Gondwana Collection Lodge"
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-10 -left-10 w-56 h-56 rounded-2xl overflow-hidden shadow-xl z-20 animate-fade-up animation-delay-500">
                    <img
                      src="/partners/WhatsApp Image 2025-02-27 at 11.48.06.jpeg"
                      alt="Namibia Tourism Board"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div id="partner-content-2" className="relative hidden">
                  <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="/partners/WhatsApp Image 2025-02-27 at 11.49.26.jpeg"
                      alt="Tour & Safari Association"
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                  <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full overflow-hidden shadow-xl z-20 border-4 border-white animate-fade-up animation-delay-500">
                    <img
                      src="/partners/WhatsApp Image 2025-02-27 at 11.51.42.jpeg"
                      alt="Save The Rhino Trust Namibia"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-10 -left-10 w-56 h-56 rounded-2xl overflow-hidden shadow-xl z-20 animate-fade-up animation-delay-700">
                    <img
                      src="/activities/wildlife.jpeg"
                      alt="Wildlife conservation"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16">
              <div id="partner-content-1" className="block">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-200">
                    <div className="flex items-start gap-4">
                      <img
                        src="/partners/WhatsApp Image 2025-02-27 at 11.49.26.jpeg"
                        alt="Tour & Safari Association"
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-xl font-display font-bold text-gray-900 mb-2">Tour & Safari Association</h4>
                        <p className="text-gray-700">
                          Industry association ensuring professional standards, ethical practices, and sustainable tourism principles are followed on all our safari tours.
                        </p>
                        <a href="https://www.tasa.na/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-amber-700 hover:text-amber-600 mt-2 text-sm font-medium">
                          Visit partner website
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-up animation-delay-300">
                    <div className="flex items-start gap-4">
                      <img
                        src="/partners/WhatsApp Image 2025-02-27 at 11.51.42.jpeg"
                        alt="Save The Rhino Trust Namibia"
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-xl font-display font-bold text-gray-900 mb-2">Save The Rhino Trust Namibia</h4>
                        <p className="text-gray-700">
                          Conservation partner dedicated to protecting Namibia's endangered desert rhinos. A portion of your safari cost contributes to their vital protection efforts.
                        </p>
                        <a href="https://www.savetherhinotrust.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-amber-700 hover:text-amber-600 mt-2 text-sm font-medium">
                          Visit partner website
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 relative overflow-hidden bg-gray-900">
          <img
            src="/About images/Hero01.jpeg"
            alt="Guest Experiences"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70 pointer-events-none"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16 animate-fade-up">
              <span className="inline-block py-1 px-3 rounded-full bg-amber-700/30 text-amber-300 text-sm font-medium mb-4">
                Guest Experiences
              </span>
              <h2 className="text-4xl font-display font-bold text-white mb-6">
                What Our Guests Say
              </h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full"></div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 justify-center">
              <div className="bg-white/95 p-8 rounded-2xl max-w-md animate-fade-up animation-delay-200">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Sarah & David</h4>
                    <p className="text-amber-700">United States</p>
                  </div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-5 h-5 text-amber-500 fill-amber-500" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 italic mb-6">
                  "Our safari with Professor Rion was the highlight of our trip to Africa. His knowledge of wildlife and passion for conservation made every moment educational and exciting."
                </p>
                <p className="text-gray-500 text-sm">Namibia Highlights Safari, April 2024</p>
              </div>

              <div className="bg-white/95 p-8 rounded-2xl max-w-md animate-fade-up animation-delay-400">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Hans & Greta</h4>
                    <p className="text-amber-700">Germany</p>
                  </div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-5 h-5 text-amber-500 fill-amber-500" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 italic mb-6">
                  "Rion's ability to speak German made our safari experience so much more comfortable. His expertise in wildlife and local culture provided insights we wouldn't have gained otherwise."
                </p>
                <p className="text-gray-500 text-sm">Big Five Safari, September 2023</p>
              </div>
            </div>

            <div className="mt-12 text-center animate-fade-up animation-delay-600">
              <Link
                to="/testimonials"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white font-medium rounded-lg hover:bg-amber-800 transition-colors"
              >
                View All Testimonials
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="animate-fade-up">
                <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
                  Our Mission
                </span>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  Conservation Through Education
                </h2>
                <div className="w-24 h-1 bg-amber-500 rounded-full mb-8"></div>

                <div className="prose prose-lg text-gray-700 max-w-none mb-10">
                  <p>
                    At Rion Wildlife Safaris, we believe that education is key to conservation. Every safari is an opportunity to learn about the delicate ecosystems of southern Africa and the importance of preserving them for future generations.
                  </p>
                  <p>
                    A portion of all safari proceeds goes directly to local conservation efforts and community development programs that support wildlife protection and sustainable tourism in the region.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 animate-fade-up animation-delay-200">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Supporting Local Communities</h4>
                      <p className="text-gray-700">We work closely with local villages and conservation groups to ensure sustainable tourism practices.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 animate-fade-up animation-delay-300">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Wildlife Conservation</h4>
                      <p className="text-gray-700">We contribute to anti-poaching efforts and habitat preservation programs throughout Namibia and Botswana.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 animate-fade-up animation-delay-400">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Eco-Friendly Practices</h4>
                      <p className="text-gray-700">Our tours minimize environmental impact while maximizing educational and conservation value.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 animate-fade-up animation-delay-300">
                <div className="rounded-xl overflow-hidden h-64">
                  <img
                    src="/activities/wildlife.jpeg"
                    alt="Wildlife conservation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-xl overflow-hidden h-64 mt-8">
                  <img
                    src="/About images/Rion03.jpeg"
                    alt="Local community"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-xl overflow-hidden h-64">
                  <img
                    src="/activities/photography.jpeg"
                    alt="Education programs"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-xl overflow-hidden h-64 mt-8">
                  <img
                    src="/Tours/safaridrive.jpg"
                    alt="Safari guides"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default About; 